import { createContext, useContext } from "react";
import type {
  ApprovalTarget,
  DeleteTarget,
  FileManagerItem,
  MoveOrCopyTarget,
  RenameTarget,
  TagsTarget,
} from "@/components/core/FileManager/FileManager.types.ts";

export interface FileManagerUIContextProps {
  renameTarget: RenameTarget | null;
  deleteTarget: DeleteTarget | null;
  moveTarget: MoveOrCopyTarget | null;
  duplicateTarget: MoveOrCopyTarget | null;
  tagsTarget: TagsTarget | null;
  approvalTarget: ApprovalTarget | null;
  detailsTarget: FileManagerItem | null;
  shareTarget: FileManagerItem | null;
  isUploadOpen: boolean;
  isCreateFolderOpen: boolean;
  clipboardItems: FileManagerItem[];

  openRename: (t: RenameTarget) => void;
  closeRename: () => void;
  openDelete: (t: DeleteTarget) => void;
  closeDelete: () => void;
  openMove: (t: MoveOrCopyTarget) => void;
  closeMove: () => void;
  openDuplicate: (t: MoveOrCopyTarget) => void;
  closeDuplicate: () => void;
  openTags: (t: TagsTarget) => void;
  closeTags: () => void;
  openApproval: (t: ApprovalTarget) => void;
  closeApproval: () => void;
  openDetails: (item: FileManagerItem) => void;
  closeDetails: () => void;
  openShare: (item: FileManagerItem) => void;
  closeShare: () => void;
  openUpload: () => void;
  closeUpload: () => void;
  copyToClipboard: (items: FileManagerItem[]) => void;
  clearClipboard: () => void;
  openCreateFolder: (targetFolderId?: string | null) => void;
  closeCreateFolder: () => void;
  createFolderTargetFolderId: string | null | undefined;

  selectedItems: FileManagerItem[];
  selectedIds: Set<string>;
  hasSelection: boolean;
  selectedCount: number;
  isSelectionMode: boolean;
  isSelected: (id: string) => boolean;
  selectItem: (item: FileManagerItem) => void;
  deselectItem: (id: string) => void;
  toggleItem: (item: FileManagerItem) => void;
  selectAll: (items: FileManagerItem[]) => void;
  deselectAll: () => void;
  toggleSelectionMode: () => void;
  enterSelectionMode: () => void;
  exitSelectionMode: () => void;

  isTreeVisible: boolean;
  toggleTree: () => void;
}

export const FileManagerUIContext = createContext<FileManagerUIContextProps | null>(null);

export const useFileManagerUI = () => {
  const ctx = useContext(FileManagerUIContext);
  if (ctx == null) {
    throw new Error("useFileManagerUI must be used within FileManagerContainer");
  }
  return ctx;
};

/**
 * Selection-aware chrome is optional so item cards can also render outside a
 * FileManagerContainer — the hub's record lists reuse them with no selection.
 */
export const useOptionalFileManagerUI = () => useContext(FileManagerUIContext);
